import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskReveal } from "./Reveal";
import { useLang } from "../../lib/LangContext";
import { IMAGES } from "../../lib/products";

export default function Hero() {
  const ref = useRef(null);
  const { dict, isRTL } = useLang();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden pt-[96px] pb-16"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('${IMAGES.hero}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.35,
            filter: "grayscale(10%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(247,245,240,0.45) 0%, rgba(247,245,240,0.85) 55%, #F7F5F0 100%)",
          }}
        />
      </motion.div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="label text-[#1A1918]"
        >
          {dict.hero_meta_left}
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1 }}
          className="label hidden md:block text-[#1A1918]"
        >
          {dict.hero_meta_right}
        </motion.div>
      </div>

      <motion.div
        style={{ y: textY }}
        className="max-w-[1600px] mx-auto px-6 md:px-10 mt-16 md:mt-24"
      >
        <MaskReveal
          testId="hero-title"
          as="h1"
          className="serif font-light tracking-[-0.02em] leading-[0.92] text-[16vw] md:text-[10.5vw] text-[#1A1918]"
          lines={[
            <>{dict.hero_title_1}</>,
            <>
              <span className="italic">{dict.hero_title_2a}</span> {dict.hero_title_2b}
            </>,
          ]}
          stagger={0.14}
          duration={1.15}
        />
      </motion.div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 mt-14 md:mt-20 grid grid-cols-12 gap-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 md:col-span-5 md:col-start-2 text-[15px] md:text-[17px] leading-[1.6] text-[#5C5A55] max-w-[42ch]"
        >
          {dict.hero_body}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.55, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 md:col-span-4 md:col-start-9 flex flex-col gap-4"
        >
          {[
            [dict.hero_stat_1_n, dict.hero_stat_1_l],
            [dict.hero_stat_2_n, dict.hero_stat_2_l],
            [dict.hero_stat_3_n, dict.hero_stat_3_l],
          ].map(([n, l], i) => (
            <div key={i} className="flex items-baseline gap-3">
              <span className="serif italic text-5xl text-[#1A1918]">{n}</span>
              <span className="label text-[#5C5A55]">{l}</span>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 1.1, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className={`absolute ${
          isRTL ? "left-4 md:left-10 lg:left-16" : "right-4 md:right-10 lg:right-16"
        } top-[40%] md:top-[46%] w-[38vw] max-w-[420px] aspect-[4/5] hidden md:block`}
      >
        <div className="relative w-full h-full">
          <div className="absolute -inset-6 bg-[radial-gradient(closest-side,rgba(26,25,24,0.10),transparent_70%)]" />
          <img
            src={IMAGES.paperCups}
            alt="Kraft paper coffee cups"
            className="relative w-full h-full object-cover spot"
          />
          <div className={`absolute -bottom-6 ${isRTL ? "right-0" : "left-0"} label text-[10px] text-[#5C5A55]`}>
            {dict.hero_fig_caption}
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <div className="label text-[10px] text-[#1A1918]">{dict.hero_scroll}</div>
        <motion.div
          animate={{ scaleY: [0.3, 1, 0.3], originY: 0 }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="w-px h-10 bg-[#1A1918]"
        />
      </motion.div>
    </section>
  );
}
